import { useEffect, Suspense, lazy } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { ThemeProvider } from "@mui/material";
import CssBaseline from "@mui/material/CssBaseline";
import { AuthProvider, useAuth } from "./features/auth/contexts/AuthContext";
import { ReservationsProvider } from "./features/reservations/contexts/ReservationsContext";
import { ZoneFilterProvider } from "./features/calendar/contexts/ZoneFilterContext";
import { TooltipProvider } from "./features/reservations/contexts/TooltipContext";
import { NotificationsProvider } from "./context/notifications";
import { MutationsProvider } from "./contexts/MutationsContext";
import {
  ThemeContextProvider,
  useThemeContext,
} from "./features/shared/contexts/ThemeContext";
import { themeLight, themeDark } from "./features/shared/styles/theme";
import DateRangeGlobalStyles from "./features/shared/components/DateRangeGlobalStyles";
import Login from "./features/auth/components/Login";
import { Calendar } from "./features/calendar";
import Reservations from "./features/reservations/components/Reservations";
import ProtectedRoute from "./features/auth/components/ProtectedRoute";
import ReservationTooltip from "./features/reservations/components/ReservationTooltip";
import LoadingFallback from "./features/shared/components/LoadingFallback";
import { useReloadOnNewVersion } from "./features/shared/hooks/useReloadOnNewVersion";
import {
  getSafeLastPath,
  rememberLastPath,
} from "./features/shared/utils/navigation";

// Lazy loading de componentes pesados
const StatisticsPage = lazy(
  () => import("./features/statistics/components/StatisticsPage")
);
const DepartmentsPage = lazy(() => import("./features/departments/page"));
const EstacionamientosPage = lazy(
  () => import("./features/estacionamiento/page")
);
const ClientsPage = lazy(() => import("./features/clients"));
const LogsPage = lazy(() => import("./features/logs/components/LogsPage"));
const InventoryPage = lazy(() => import("./features/inventory/page"));
const CleaningPage = lazy(() => import("./features/cleaning/CleaningPage"));
const CleaningStaffPage = lazy(() => import("./features/cleaning/CleaningStaffPage"));
const InventoryDetailPage = lazy(() => import("./features/inventory/detail"));
const MailPage = lazy(() => import("./features/mail/MailPage"));

// ThemeBridge: consume mode desde ThemeContext y construye el theme MUI
const ThemeBridge = ({ children }: { children: React.ReactNode }) => {
  const { mode } = useThemeContext();

  // Validar que mode esté definido
  if (!mode) {
    console.error("ThemeBridge: mode no está definido");
    return (
      <ThemeProvider theme={themeLight}>
        <CssBaseline />
        <DateRangeGlobalStyles theme={themeLight} />
        {children}
      </ThemeProvider>
    );
  }

  const currentTheme = mode === "dark" ? themeDark : themeLight;

  return (
    <ThemeProvider theme={currentTheme}>
      <CssBaseline />
      <DateRangeGlobalStyles theme={currentTheme} />
      {children}
    </ThemeProvider>
  );
};

const AppRoutes = () => {
  const { isAuthenticated, user } = useAuth();
  const location = useLocation();
  const isCleaningStaff = user?.rol === "limpieza";
  const safeLastPath = getSafeLastPath(user?.rol);
  const renderAdminRoute = (children: React.ReactNode) => (
    <ProtectedRoute>
      {isCleaningStaff ? <Navigate to="/limpieza-diaria" replace /> : children}
    </ProtectedRoute>
  );

  // Los logs exponen datos de todos los clientes sin redactar, así que el
  // backend los limita a super_admin y admin. La ruta acompaña esa regla para
  // que nadie llegue a una pantalla que sólo le va a devolver 403.
  const canReadLogs = user?.rol === "super_admin" || user?.rol === "admin";
  const renderLogsRoute = (children: React.ReactNode) => (
    <ProtectedRoute>
      {canReadLogs ? children : <Navigate to={isCleaningStaff ? "/limpieza-diaria" : "/calendario"} replace />}
    </ProtectedRoute>
  );

  useEffect(() => {
    rememberLastPath(location);
  }, [location]);

  return (
    <Routes>
      <Route
        path="/login"
        element={
          isAuthenticated ? (
            <Navigate to={safeLastPath} replace />
          ) : (
            <Login />
          )
        }
      />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Navigate
              to={safeLastPath}
              replace
            />
          </ProtectedRoute>
        }
      />
      <Route
        path="/calendario"
        element={renderAdminRoute(
            <ReservationsProvider>
              <Calendar />
            </ReservationsProvider>
        )}
      />
      <Route
        path="/reservas"
        element={renderAdminRoute(
            <Reservations />
        )}
      />
      <Route
        path="/nueva-reserva"
        element={renderAdminRoute(
            <Navigate to="/calendario" replace />
        )}
      />
      <Route
        path="/estadisticas"
        element={renderAdminRoute(
            <Suspense
              fallback={
                <LoadingFallback message="Cargando estadísticas..." />
              }
            >
              <StatisticsPage />
            </Suspense>
        )}
      />
      <Route
        path="/departamentos"
        element={renderAdminRoute(
            <Suspense
              fallback={
                <LoadingFallback message="Cargando departamentos..." />
              }
            >
              <DepartmentsPage />
            </Suspense>
        )}
      />
      <Route
        path="/estacionamientos"
        element={renderAdminRoute(
            <Suspense
              fallback={
                <LoadingFallback message="Cargando estacionamientos..." />
              }
            >
              <EstacionamientosPage />
            </Suspense>
        )}
      />
      <Route
        path="/clientes"
        element={renderAdminRoute(
            <Suspense
              fallback={<LoadingFallback message="Cargando clientes..." />}
            >
              <ClientsPage />
            </Suspense>
        )}
      />
      <Route
        path="/logs"
        element={renderLogsRoute(
            <Suspense
              fallback={<LoadingFallback message="Cargando logs..." />}
            >
              <LogsPage />
            </Suspense>
        )}
      />
      <Route
        path="/inventario"
        element={renderAdminRoute(
            <Suspense
              fallback={<LoadingFallback message="Cargando inventario..." />}
            >
              <InventoryPage />
            </Suspense>
        )}
      />
      <Route
        path="/limpieza"
        element={renderAdminRoute(
            <Suspense
              fallback={<LoadingFallback message="Cargando limpieza..." />}
            >
              <CleaningPage />
            </Suspense>
        )}
      />
      <Route
        path="/limpieza-diaria"
        element={
          <ProtectedRoute>
            <Suspense
              fallback={<LoadingFallback message="Cargando limpieza diaria..." />}
            >
              <CleaningStaffPage />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/correo"
        element={renderAdminRoute(
            <Suspense
              fallback={<LoadingFallback message="Cargando correo..." />}
            >
              <MailPage />
            </Suspense>
        )}
      />
      <Route
        path="/inventario/:checklistId"
        element={renderAdminRoute(
            <Suspense
              fallback={<LoadingFallback message="Cargando revisión..." />}
            >
              <InventoryDetailPage />
            </Suspense>
        )}
      />
      <Route
        path="*"
        element={
          <ProtectedRoute>
            <Navigate to={safeLastPath} replace />
          </ProtectedRoute>
        }
      />
    </Routes>
  );
};

const App = () => {
  useReloadOnNewVersion();

  return (
    <ThemeContextProvider>
      <ThemeBridge>
        <Router>
          <AuthProvider>
            <MutationsProvider>
              <ZoneFilterProvider>
                <TooltipProvider>
                  <NotificationsProvider>
                    <AppRoutes />
                    <ReservationTooltip />
                  </NotificationsProvider>
                </TooltipProvider>
              </ZoneFilterProvider>
            </MutationsProvider>
          </AuthProvider>
        </Router>
      </ThemeBridge>
    </ThemeContextProvider>
  );
};

export default App;
